import { useCallback, useState } from "react";
import { MdPaid } from "react-icons/md";

import {
  AskStrikeUpButton,
  Card,
  DateRangePicker,
  Flex,
  Heading,
  HStack,
  RefreshButton,
  Select,
  Spinner,
  Stack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { AiCostPlaneValue } from "services/aiGatewayService";
import { getThemeColor } from "theme/colors";

import { useDashboardRange } from "../useDashboardRange";
import CostsPanel from "./CostsPanel";
import UsagePanel from "./UsagePanel";

/**
 * Dashboard → AI Spend: what AI usage cost (by model, stage and widget, day by day) and who spent
 * it (by customer company, customer, source, workflow, autonomous agent and run).
 *
 * Shown to every tenant — spend is metered for all companies, with or without the AI Gateway
 * feature. The date range is the Dashboard's shared one (`useDashboardRange`, persisted), so it
 * follows the reader from Overview / Usage / Performance; the server caps the scanned span at 90
 * days back from the range's end. The plane ("Usage") select is page-level too, so both cards
 * always describe the same calls.
 */
const AiSpendPage = () => {
  const { t } = useTranslation();
  const [refreshToken, setRefreshToken] = useState(0);
  const [plane, setPlane] = useState<AiCostPlaneValue>(AiCostPlaneValue.Tenant);
  // Each panel owns its query; it reports the state up so the card header can show the same
  // refresh spinner the other Dashboard pages use, and the Refresh button can spin with it.
  const [spendLoading, setSpendLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(true);
  const onSpendLoading = useCallback((v: boolean) => setSpendLoading(v), []);
  const onUsageLoading = useCallback((v: boolean) => setUsageLoading(v), []);
  const { range, setPreset, setCustom, startDate, endDate } =
    useDashboardRange();
  const reportRange = { startDate, endDate };

  const planeItems = [
    {
      value: String(AiCostPlaneValue.Tenant),
      label: t("app.aiGateway.costs.plane.tenant"),
    },
    {
      value: String(AiCostPlaneValue.Platform),
      label: t("app.aiGateway.costs.plane.platform"),
    },
    {
      value: String(AiCostPlaneValue.All),
      label: t("app.aiGateway.costs.plane.all"),
    },
  ];

  return (
    <Stack gap={6} w="full" maxW="full">
      <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
        <HStack>
          <MdPaid size={32} color={getThemeColor("icon").base} />
          <Heading
            size="2xl"
            fontWeight="bold"
            color={{ base: "navy.700", _dark: "white" }}
          >
            {t("app.aiSpend.title")}
          </Heading>
        </HStack>
        <HStack gap={3} wrap="wrap">
          <AskStrikeUpButton context={t("app.askStrikeUp.context.dashboard")} />
          <Select
            items={planeItems}
            value={[String(plane)]}
            onValueChange={(d: { value: string[] }) =>
              setPlane(Number(d.value[0]) as AiCostPlaneValue)
            }
            size="sm"
            width="200px"
          />
          <DateRangePicker
            preset={range.preset}
            from={range.from}
            to={range.to}
            onPreset={setPreset}
            onCustom={setCustom}
            labels={{
              last7: t("app.dashboard.timeRange.last7Days"),
              last14: t("app.dashboard.timeRange.last14Days"),
              last30: t("app.dashboard.timeRange.last30Days"),
              custom: t("app.dashboard.timeRange.custom"),
              from: t("app.dashboard.timeRange.from"),
              to: t("app.dashboard.timeRange.to"),
            }}
          />
          <RefreshButton
            onClick={() => setRefreshToken((token) => token + 1)}
            loading={spendLoading || usageLoading}
          />
        </HStack>
      </Flex>

      <Card.Root w="full">
        <Card.Header pb={0}>
          <Flex justify="space-between" align="flex-start" gap={3}>
            <Heading size="md">{t("app.aiSpend.sections.spend")}</Heading>
            {spendLoading && (
              <Spinner size="sm" color="var(--chakra-colors-blue-solid)" />
            )}
          </Flex>
        </Card.Header>
        <Card.Body>
          <CostsPanel
            refreshToken={refreshToken}
            range={reportRange}
            plane={plane}
            onLoadingChange={onSpendLoading}
          />
        </Card.Body>
      </Card.Root>

      <Card.Root w="full">
        <Card.Header pb={0}>
          <Flex justify="space-between" align="flex-start" gap={3}>
            <Heading size="md">{t("app.aiSpend.sections.usage")}</Heading>
            {usageLoading && (
              <Spinner size="sm" color="var(--chakra-colors-blue-solid)" />
            )}
          </Flex>
        </Card.Header>
        <Card.Body>
          <UsagePanel
            refreshToken={refreshToken}
            range={reportRange}
            plane={plane}
            showKpis={false}
            onLoadingChange={onUsageLoading}
          />
        </Card.Body>
      </Card.Root>
    </Stack>
  );
};

export default AiSpendPage;
